/**
 * Turning what the speech recogniser heard into text that reads like notes.
 *
 * The Web Speech API hands back a bare transcript: no separator from what is
 * already written, a first word that may or may not be capitalised, and —
 * depending on the browser and the language — punctuation still spoken as
 * words ("punto y seguido") rather than written as marks. Dropped into the
 * note as-is that reads as one long run-on line, which is what makes dictated
 * notes look like the punctuation is broken.
 *
 * Pure text in, pure text out: the editor decides *where* dictated text goes,
 * this module only decides how it should read once it gets there.
 */

/** The languages the app dictates in, as the therapist profile stores them. */
export type DictationLanguage = "es" | "en";

/**
 * The dictation language behind a language tag, from either the profile's
 * "es"/"en" or a recogniser locale like "en-US". Spanish is the fallback: it is
 * what the app speaks when nothing says otherwise.
 */
export function dictationLanguageOf(tag: string): DictationLanguage {
    return tag.toLowerCase().startsWith("en") ? "en" : "es";
}

type SpokenPunctuation = readonly [phrase: RegExp, mark: string, say: string];

/**
 * Spoken punctuation that the recogniser returns as words, with the phrase to
 * tell the therapist to say — the one the pattern is sure to match, when it
 * accepts several.
 *
 * Only phrases that cannot plausibly be part of a clinical note are listed.
 * Bare "coma", "punto" and "period" are deliberately absent: a patient can be
 * *en coma*, a therapist writes "hasta cierto punto", and "period" is half of
 * what an intake note says about a cycle. Turning those into marks would
 * silently delete words from the record, which is worse than leaving a mark
 * for the therapist to type.
 */
const SPOKEN_PUNCTUATION: Record<DictationLanguage, readonly SpokenPunctuation[]> = {
    es: [
        [/\bpuntos suspensivos\b/gi, "…", "puntos suspensivos"],
        [/\bpunto y coma\b/gi, ";", "punto y coma"],
        [/\bdos puntos\b/gi, ":", "dos puntos"],
        [/\bpunto y (?:aparte|seguido)\b/gi, ".", "punto y seguido"],
        [/\bpunto final\b/gi, ".", "punto final"],
        [/\b(?:abre|abrir) interrogaci[oó]n\b/gi, "¿", "abre interrogación"],
        [/\b(?:cierra|cerrar|signo de) interrogaci[oó]n\b/gi, "?", "cierra interrogación"],
        [/\b(?:abre|abrir) (?:exclamaci[oó]n|admiraci[oó]n)\b/gi, "¡", "abre exclamación"],
        [/\b(?:cierra|cerrar|signo de) (?:exclamaci[oó]n|admiraci[oó]n)\b/gi, "!", "cierra exclamación"],
        [/\b(?:abre|abrir) par[eé]ntesis\b/gi, "(", "abre paréntesis"],
        [/\b(?:cierra|cerrar) par[eé]ntesis\b/gi, ")", "cierra paréntesis"],
        [/\b(?:abre|abrir) comillas\b/gi, '"', "abre comillas"],
        [/\b(?:cierra|cerrar) comillas\b/gi, '"', "cierra comillas"],
    ],
    en: [
        [/\bellipsis\b/gi, "…", "ellipsis"],
        [/\bsemicolon\b/gi, ";", "semicolon"],
        [/\bfull stop\b/gi, ".", "full stop"],
        [/\bquestion mark\b/gi, "?", "question mark"],
        [/\bexclamation (?:mark|point)\b/gi, "!", "exclamation mark"],
        [/\bopen(?:ing)? paren(?:thesis)?\b/gi, "(", "open parenthesis"],
        [/\bclos(?:e|ing) paren(?:thesis)?\b/gi, ")", "close parenthesis"],
        [/\bopen(?:ing)? quotes?\b/gi, '"', "open quote"],
        [/\bclos(?:e|ing) quotes?\b/gi, '"', "close quote"],
    ],
};

/** A punctuation mark the therapist can dictate, and what to say for it. */
export type DictationCommand = { say: string; mark: string };

/**
 * What to say to dictate each mark, for telling the therapist. Read from the
 * same list that does the converting, so the help cannot promise a phrase the
 * dictation does not understand.
 */
export function dictationCommandsFor(language: DictationLanguage): DictationCommand[] {
    return (SPOKEN_PUNCTUATION[language] ?? SPOKEN_PUNCTUATION.es).map(
        ([, mark, say]) => ({ say, mark })
    );
}

/** Marks that close up against the word before them. */
const CLOSING_MARKS = ',.;:?!…)"»';
/** Marks that open against the word after them. */
const OPENING_MARKS = '¿¡(«"';
/** Marks that end a sentence, so the next word starts a new one. */
const SENTENCE_END = /[.?!…]/;

/** Upper-cases the first letter, looking past any opening mark before it. */
function capitaliseFirstLetter(text: string): string {
    const index = [...text].findIndex((character) => character.toLowerCase() !== character.toUpperCase());
    if (index === -1) return text;
    return text.slice(0, index) + text[index].toUpperCase() + text.slice(index + 1);
}

/** Puts the spacing around marks back the way a reader expects it. */
function tidyPunctuationSpacing(text: string): string {
    return (
        text
            .replace(/\s+/g, " ")
            // "se enfada : llora" → "se enfada: llora"
            .replace(new RegExp(`\\s+([${escapeForClass(CLOSING_MARKS)}])`, "g"), "$1")
            // "¿ cómo estás" → "¿cómo estás"
            .replace(new RegExp(`([${escapeForClass(OPENING_MARKS)}])\\s+`, "g"), "$1")
            // "llega tarde.se disculpa" → "llega tarde. se disculpa", while
            // "7.5" is left alone because a digit follows the mark.
            .replace(/([,.;:?!…])(?=[^\s\d,.;:?!…)"»])/g, "$1 ")
            // A new sentence inside the same transcript still starts in caps.
            .replace(/([.?!…]\s+)(\p{Ll})/gu, (_, lead: string, letter: string) => lead + letter.toUpperCase())
    );
}

function escapeForClass(characters: string): string {
    return characters.replace(/[\\\]^-]/g, "\\$&");
}

/**
 * The transcript as it should be written: spoken marks become marks, and the
 * spacing around them is the spacing of written Spanish or English.
 *
 * Capitalising the *first* word is left to `joinDictatedText`, which is the
 * only place that knows whether this text opens a sentence or continues one.
 */
export function normalizeDictatedText(raw: string, language: DictationLanguage): string {
    const spoken = SPOKEN_PUNCTUATION[language] ?? SPOKEN_PUNCTUATION.es;
    const written = spoken.reduce<string>(
        (text, [phrase, mark]) => text.replace(phrase, mark),
        raw
    );
    return tidyPunctuationSpacing(written).trim();
}

/**
 * The exact string to write into the note, given what is already before it.
 *
 * Carries its own leading space — the caller must not add one, since whether
 * a space belongs there depends on what the therapist has already written and
 * on whether the new text starts with a mark that closes up against it.
 *
 * A word heard mid-sentence keeps the case the recogniser gave it: lowering it
 * would break a name it got right, which costs more than an odd capital.
 */
export function joinDictatedText(precedingText: string, chunk: string): string {
    const text = chunk.trim();
    if (!text) return "";

    const lastCharacter = precedingText.slice(-1);
    const lastVisible = precedingText.trimEnd().slice(-1);

    const needsSpace =
        lastCharacter !== "" &&
        !/\s/.test(lastCharacter) &&
        !OPENING_MARKS.includes(lastCharacter) &&
        !CLOSING_MARKS.includes(text[0]);

    const opensSentence =
        lastVisible === "" ||
        SENTENCE_END.test(lastVisible) ||
        /\n\s*$/.test(precedingText);

    const body = opensSentence ? capitaliseFirstLetter(text) : text;
    return needsSpace ? ` ${body}` : body;
}

/** Text with a dictated phrase written into it, and where the next one goes. */
export type DictatedInsertion = {
    text: string;
    /** Just past what was written, so consecutive phrases read in order. */
    caret: number;
};

/**
 * Writes a dictated phrase into `existing` at `at` — where the therapist left
 * the cursor — rather than at the end.
 *
 * The phrase is spaced against both sides: `joinDictatedText` handles the text
 * before it, and a separator is added after it only when the text that follows
 * would otherwise be glued to it. The separator stays *after* the returned
 * caret, so the next phrase continues right where this one ended instead of
 * jumping over it.
 */
export function insertDictatedText(
    existing: string,
    at: number,
    chunk: string
): DictatedInsertion {
    const position = Number.isFinite(at)
        ? Math.max(0, Math.min(at, existing.length))
        : existing.length;
    const before = existing.slice(0, position);
    const after = existing.slice(position);

    const written = joinDictatedText(before, chunk);
    if (!written) return { text: existing, caret: position };

    const following = after.slice(0, 1);
    const needsSpace =
        following !== "" && !/\s/.test(following) && !CLOSING_MARKS.includes(following);

    return {
        text: `${before}${written}${needsSpace ? " " : ""}${after}`,
        caret: position + written.length,
    };
}
